import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ROUTE_CLIENT_LOGIN,
  ROUTE_VIDEO_DETAILS,
  ROUTE_MAIN,
  ROUTE_SIGNIN_NEW,
  ROUTE_VERIFY_MOBILE,
  ROUTE_JOBS_DASHBOARD,
  ROUTE_WHAT_YOUR_NAME,
  ROUTE_LOOKING_FOR,
  ROUTE_EDUCATION_NEW,
  ROUTE_EXPERIENCE_NEW,
  ROUTE_PROFILER,
  ROUTE_CRAFTING_VIDEO,
  ROUTE_WELCOME_NEW,
  ROUTE_REGISTER_COMPLETE,
  ROUTE_SHARE_PROFILE,
  ROUTE_CANDIDATE_PROFILE,
  ROUTE_PRIVACY_POLICY,
  ROUTE_TERMS_AND_CONDITIONS,
} from "../constants/routes";
import { ACCESS_TOKEN } from "../constants/AppVarConstants";
import { fetchUserProfile } from "../redux/actions/appActions";
import AppLoader from "../components/common/AppLoader";
import { enableNotifications } from "../notifications";

const publicRoutes = [
  ROUTE_VERIFY_MOBILE,
  ROUTE_SIGNIN_NEW,
  ROUTE_CLIENT_LOGIN,
  ROUTE_REGISTER_COMPLETE,
  ROUTE_PRIVACY_POLICY,
  ROUTE_TERMS_AND_CONDITIONS,
];

const AuthWrapper = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const accessToken = localStorage.getItem(ACCESS_TOKEN);
  const userProfile = useSelector((state: any) => state.app);

  const [loading, setLoading] = useState(true);

  // Check if route is public (exact match or dynamic routes like shareprofile)
  const isPublicRoute = publicRoutes.includes(location.pathname) || 
    location.pathname.startsWith('/shareprofile/') || 
    location.pathname.startsWith('/candidate-profile/') ||
    location.pathname.startsWith('/view-job/') ||
    location.pathname === '/view-job' ||
    location.pathname.startsWith('/public-job-details/');

  // Check if route is a share profile route or view-job route (should not redirect logged-in users)
  const isShareProfileRoute = location.pathname.startsWith('/shareprofile/') || 
    location.pathname.startsWith('/candidate-profile/') ||
    location.pathname.startsWith('/view-job/') ||
    location.pathname === '/view-job' ||
    location.pathname.startsWith('/public-job-details/');

  const seekerRedirection = () => {
    const status = userProfile?.user?.user?.profileStatus || userProfile?.user?.profileStatus;
    if (status?.profileStatus === "INACTIVE") return ROUTE_WHAT_YOUR_NAME;
    // preferenceStatus is optional - skip redirect to Looking For
    if (status?.educationStatus === "INACTIVE") return ROUTE_EDUCATION_NEW;
    if (status?.experienceStatus === "INACTIVE") return ROUTE_EXPERIENCE_NEW;
    if (status?.examStatus === "INACTIVE") return ROUTE_PROFILER;
    if (status?.videoStatus === "INACTIVE") return ROUTE_CRAFTING_VIDEO;
    return ROUTE_WELCOME_NEW;
  };

  const roleBasedRedirection = () => {
    const roles = localStorage.getItem("roles");
    if (!roles || roles.length === 0 || roles.includes("job_seeker")) {
      return seekerRedirection();
    } else if (["seevi_admin"].includes(roles)) {
      return ROUTE_VIDEO_DETAILS;
    } else {
      return ROUTE_JOBS_DASHBOARD;
    }
  };

  useEffect(() => {
    if (!accessToken) {
      if (!isPublicRoute) navigate(ROUTE_MAIN);
      setLoading(false);
      return;
    }

    // If token exists → fetch profile
    dispatch(
      fetchUserProfile(
        () => {
          enableNotifications();

          // ✅ profile fetched, now we can redirect if needed
          // Only navigate if we're on a public route BUT NOT a share profile route
          // Share profile routes should be accessible to logged-in users without redirect
          if (isPublicRoute && !isShareProfileRoute) {
            navigate(roleBasedRedirection());
          }
          setLoading(false);
        },
        () => {
          // On error → force to login
          navigate(ROUTE_SIGNIN_NEW);
          setLoading(false);
        }
      )
    );
  }, [dispatch, accessToken, isPublicRoute, isShareProfileRoute, location.pathname, location.search, navigate]);

  // ✅ Loading state while fetching profile
  if (loading) {
    return <AppLoader />;
  }

  // ✅ Allow access to public routes
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // ✅ After successful login & profile fetch → render children
  return <>{children}</>;
};

export default AuthWrapper;
